/**
 * MessageBoard — reusable community message board / guest book.
 *
 * Props:
 *   • listPath   — GET  URL for messages, e.g. `/organizations/${slug}/messages`
 *   • postPath   — POST URL for a new message, e.g. `/organizations/${slug}/messages`
 *   • deletePath — DELETE URL pattern with `{id}` placeholder,
 *                  e.g. `/organizations/messages/{id}`
 *   • title      — heading (default: "Community message board")
 *   • subtitle   — subheading
 *   • testid     — data-testid root
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, User, Send, Trash2, MessagesSquare } from "lucide-react";
import { http } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

function _ago(iso) {
  if (!iso) return "";
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  if (s < 604800) return `${Math.floor(s / 86400)}d ago`;
  return new Date(iso).toLocaleDateString();
}

export default function MessageBoard({
  listPath, postPath, deletePath,
  title    = "Community message board",
  subtitle = "Share your experience and encourage others.",
  testid   = "message-board",
}) {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [loading,  setLoading]  = useState(true);

  useEffect(() => {
    setLoading(true);
    http.get(listPath)
      .then((r) => setMessages(r.data.items || []))
      .catch(() => setMessages([]))
      .finally(() => setLoading(false));
  }, [listPath]);

  const post = async (text, done) => {
    if (!user) { toast.error("Sign in to leave a message"); return; }
    try {
      const { data: m } = await http.post(postPath, { text });
      setMessages((cur) => [m, ...cur]);
      toast.success("Message posted");
      done?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't post message");
    }
  };

  const remove = async (id) => {
    try {
      await http.delete(deletePath.replace("{id}", id));
      setMessages((cur) => cur.filter((x) => x.id !== id));
      toast.success("Deleted");
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't delete"); }
  };

  return (
    <section className="mb-16" data-testid={testid}>
      <div className="flex items-center gap-2 mb-3">
        <MessagesSquare className="w-5 h-5" style={{ color: "var(--wofy-primary)" }} />
        <h2 className="font-display text-2xl">{title}</h2>
      </div>
      {subtitle && <p className="text-sm opacity-70 mb-4">{subtitle}</p>}

      <Composer disabled={!user} onSubmit={post} testid={`${testid}-composer`} />

      {loading ? (
        <div className="inline-flex items-center gap-2 opacity-60 text-sm mt-3">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading messages…
        </div>
      ) : messages.length === 0 ? (
        <div className="wofy-card !p-6 opacity-70 text-sm mt-3" data-testid={`${testid}-empty`}>
          No messages yet — be the first.
        </div>
      ) : (
        <ul className="mt-4 space-y-3" data-testid={`${testid}-list`}>
          {messages.map((m) => (
            <li key={m.id}
                className="rounded-2xl border p-3 flex items-start gap-3"
                style={{ borderColor: "var(--wofy-border)" }}
                data-testid={`${testid}-item-${m.id}`}>
              {m.user_avatar ? (
                <img src={m.user_avatar} alt="" className="w-9 h-9 rounded-full object-cover flex-shrink-0" />
              ) : (
                <div className="w-9 h-9 rounded-full grid place-items-center flex-shrink-0"
                     style={{ background: "var(--wofy-surface)" }}>
                  <User className="w-4 h-4 opacity-50" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 text-xs opacity-70">
                  <span className="font-medium">{m.user_name}</span>
                  {m.user_handle && <span>@{m.user_handle}</span>}
                  <span>·</span>
                  <span>{_ago(m.created_at)}</span>
                  {user?.id === m.user_id && (
                    <button type="button" onClick={() => remove(m.id)}
                            className="ml-auto opacity-60 hover:opacity-100"
                            aria-label="Delete"
                            data-testid={`${testid}-delete-${m.id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
                <div className="mt-1 opacity-90 whitespace-pre-line">{m.text}</div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function Composer({ disabled, onSubmit, testid }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    const t = text.trim();
    if (t.length < 2) return;
    setBusy(true);
    await onSubmit(t, () => setText(""));
    setBusy(false);
  };
  return (
    <form onSubmit={submit} className="rounded-2xl border p-3"
          style={{ borderColor: "var(--wofy-border)" }}
          data-testid={testid}>
      <textarea
        rows={3} maxLength={1500}
        value={text} onChange={(e) => setText(e.target.value)}
        placeholder={disabled ? "Sign in to leave a message" : "Leave a message…"}
        disabled={disabled}
        className="w-full outline-none resize-y bg-transparent text-sm"
        data-testid={`${testid}-input`}
      />
      <div className="flex items-center justify-between mt-1">
        <div className="text-[11px] opacity-60">{text.length}/1500</div>
        <button type="submit" disabled={disabled || busy || text.trim().length < 2}
                className="wofy-btn-primary text-sm"
                data-testid={`${testid}-submit`}>
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          Post message
        </button>
      </div>
    </form>
  );
}
